import type { ReactNode } from "react";

/**
 * Bouwstenen voor onderzoeksinfographics. Alles wordt als semantische HTML en
 * inline SVG gerenderd (geen plaatjes), inclusief een verborgen datatabel per
 * grafiek zodat de cijfers ook zonder CSS of voor crawlers leesbaar zijn.
 */

export function Infographic({
  id,
  kicker,
  title,
  description,
  source,
  children,
}: {
  id: string;
  kicker?: string;
  title: string;
  description?: string;
  source: string;
  children: ReactNode;
}) {
  return (
    <figure
      id={id}
      className="card-surface scroll-mt-28 overflow-hidden rounded-xl border border-border bg-card"
      role="group"
      aria-labelledby={`${id}-title`}
    >
      <div className="border-b border-border bg-secondary/40 px-5 py-4">
        {kicker ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-success">{kicker}</p>
        ) : null}
        <h3 id={`${id}-title`} className="mt-1 text-base font-semibold text-foreground sm:text-lg">
          {title}
        </h3>
        {description ? <p className="mt-1.5 max-w-3xl text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <div className="px-5 py-5">{children}</div>
      <figcaption className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Bron:</span> {source}
      </figcaption>
    </figure>
  );
}

export type BarItem = {
  label: string;
  value: number;
  valueLabel: string;
  hint?: string;
  highlight?: boolean;
};

/** Horizontale staafgrafiek met zichtbare labels en een verborgen datatabel. */
export function BarChart({
  items,
  caption,
  unitLabel,
  max,
}: {
  items: BarItem[];
  caption: string;
  unitLabel: string;
  max?: number;
}) {
  const peak = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <div>
      <ul className="space-y-3" aria-hidden>
        {items.map((item) => (
          <li key={item.label}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className={item.highlight ? "font-semibold text-foreground" : "text-foreground"}>
                {item.label}
              </span>
              <span className="tabular-nums text-muted-foreground">{item.valueLabel}</span>
            </div>
            <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${item.highlight ? "bg-success" : "bg-primary/70"}`}
                style={{ width: `${Math.max(2, (item.value / peak) * 100)}%` }}
              />
            </div>
            {item.hint ? <p className="mt-1 text-xs text-muted-foreground">{item.hint}</p> : null}
          </li>
        ))}
      </ul>
      <DataTable
        caption={caption}
        head={["Betaalmethode", unitLabel, "Toelichting"]}
        rows={items.map((i) => [i.label, i.valueLabel, i.hint ?? "—"])}
      />
    </div>
  );
}

/** Min–mediaan–max staafjes: laat spreiding van meetwaarden zien. */
export function RangeChart({
  items,
  caption,
  unitLabel,
}: {
  items: { label: string; min: number; median: number; max: number; unit: string }[];
  caption: string;
  unitLabel: string;
}) {
  const peak = Math.max(...items.map((i) => i.max), 1);
  return (
    <div>
      <ul className="space-y-4" aria-hidden>
        {items.map((item) => {
          const left = (item.min / peak) * 100;
          const width = Math.max(1.5, ((item.max - item.min) / peak) * 100);
          const marker = (item.median / peak) * 100;
          return (
            <li key={item.label}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-foreground">{item.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {item.min}–{item.max} {item.unit} · mediaan {item.median} {item.unit}
                </span>
              </div>
              <div className="relative mt-2 h-3 w-full rounded-full bg-muted">
                <div
                  className="absolute top-0 h-3 rounded-full bg-primary/30"
                  style={{ left: `${left}%`, width: `${width}%` }}
                />
                <div
                  className="absolute top-[-2px] h-[18px] w-[3px] rounded-full bg-success"
                  style={{ left: `calc(${marker}% - 1.5px)` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
      <DataTable
        caption={caption}
        head={["Reeks", `Snelste (${unitLabel})`, `Mediaan (${unitLabel})`, `Langzaamste (${unitLabel})`]}
        rows={items.map((i) => [i.label, `${i.min}`, `${i.median}`, `${i.max}`])}
      />
    </div>
  );
}

/** Donut met één percentage, als SVG. */
export function DonutChart({
  percentage,
  label,
  sublabel,
}: {
  percentage: number;
  label: string;
  sublabel: string;
}) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const filled = (Math.min(100, Math.max(0, percentage)) / 100) * c;
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 100 100" className="h-24 w-24 shrink-0 -rotate-90" role="img" aria-label={`${label}: ${percentage}%`}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--muted)" strokeWidth="10" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="var(--success)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${c - filled}`}
        />
      </svg>
      <div>
        <p className="text-2xl font-bold tabular-nums text-foreground">{percentage}%</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{sublabel}</p>
      </div>
    </div>
  );
}

/** Histogram met verticale kolommen (bijv. verdeling van meetwaarden). */
export function ColumnChart({
  items,
  caption,
  axisLabel,
  unitLabel,
}: {
  items: { label: string; value: number; hint?: string }[];
  caption: string;
  axisLabel: string;
  unitLabel: string;
}) {
  const peak = Math.max(...items.map((i) => i.value), 1);
  return (
    <div>
      <div className="flex h-40 items-end gap-2" aria-hidden>
        {items.map((item) => (
          <div key={item.label} className="flex h-full flex-1 flex-col justify-end text-center">
            <span className="mb-1 text-xs font-semibold tabular-nums text-foreground">{item.value}</span>
            <div
              className="mx-auto w-full rounded-t-md bg-primary/70"
              style={{ height: `${Math.max(4, (item.value / peak) * 100)}%` }}
            />
            <span className="mt-1.5 text-[11px] leading-tight text-muted-foreground">{item.label}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{axisLabel}</p>
      <DataTable
        caption={caption}
        head={[axisLabel, unitLabel]}
        rows={items.map((i) => [i.label, `${i.value}`])}
      />
    </div>
  );
}

/** Genummerde processtroom (bijv. de route van geld door een betaalmethode). */
export function FlowDiagram({ steps }: { steps: { title: string; text: string }[] }) {
  return (
    <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((step, i) => (
        <li key={step.title} className="relative rounded-lg border border-border bg-secondary/40 p-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {i + 1}
          </span>
          <p className="mt-2.5 text-sm font-semibold text-foreground">{step.title}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.text}</p>
        </li>
      ))}
    </ol>
  );
}

/** Verborgen maar volwaardige datatabel onder elke grafiek. */
export function DataTable({
  caption,
  head,
  rows,
}: {
  caption: string;
  head: string[];
  rows: string[][];
}) {
  return (
    <div className="sr-only">
    <table>
      <caption>{caption}</caption>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.join("|")}>
            {row.map((cell, i) =>
              i === 0 ? (
                <th key={i} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={i}>{cell}</td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}
